// GDPR / CCPA consent banner editor (audit Phase 3 — Compliance).
//
// Drives the four backend endpoints in ``backend/server.py``:
//   GET  /api/consent/config          → load the workspace's banner config
//   PUT  /api/consent/config          → patch the banner config (PATCH-style)
//   GET  /api/consent/records         → recent accept/reject records
//   POST /api/public/consent/record   → exercised by the embeddable widget,
//                                       not this UI; documented here for
//                                       operators who want to test the
//                                       roundtrip from a landing page.
//
// The widget itself ships separately — operators drop a script tag onto
// their landing pages that hits ``/api/public/consent/config`` to
// decide whether to render. This editor is the operator-side
// configuration surface.

import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Cookie, ShieldCheck, Save, Plus, Trash2 } from 'lucide-react';
import XpnsrPageHeader from './XpnsrPageHeader';
import ConsentEmbed from './ConsentEmbed';

const REGION_PRESETS = [
  { value: 'eea_uk_ch', label: 'EEA + UK + Switzerland (recommended)' },
  { value: 'eea', label: 'EEA only (EU 27 + EFTA)' },
  { value: 'global', label: 'Global (show to every visitor)' },
];

const LAYOUTS = [
  { value: 'bottom', label: 'Bottom bar' },
  { value: 'top', label: 'Top bar' },
  { value: 'modal', label: 'Modal dialog' },
];

const DEFAULT_FORM = {
  enabled: false,
  region_preset: 'eea_uk_ch',
  extra_countries: [],
  title: 'We use cookies',
  body:
    "We use cookies and similar technologies to deliver, maintain, and improve our services. " +
    "Click 'Accept' to consent or 'Reject' to limit processing to strictly necessary tracking.",
  accept_btn_text: 'Accept',
  reject_btn_text: 'Reject',
  privacy_policy_url: '',
  layout: 'bottom',
  retention_days: 365,
  vendors: [],
};

export default function ConsentBanner() {
  const { t } = useT();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [extraCountriesInput, setExtraCountriesInput] = useState('');
  const [records, setRecords] = useState([]);

  const decisionCounts = useMemo(() => {
    const counts = { accepted: 0, rejected: 0, dismissed: 0 };
    records.forEach((r) => {
      if (counts[r.decision] !== undefined) counts[r.decision] += 1;
    });
    return counts;
  }, [records]);

  const loadConfig = async () => {
    try {
      const r = await axios.get(`${API}/consent/config`);
      const cfg = r.data || {};
      setForm({
        enabled: !!cfg.enabled,
        region_preset: cfg.region_preset || 'eea_uk_ch',
        extra_countries: Array.isArray(cfg.extra_countries) ? cfg.extra_countries : [],
        title: cfg.title || '',
        body: cfg.body || '',
        accept_btn_text: cfg.accept_btn_text || 'Accept',
        reject_btn_text: cfg.reject_btn_text || 'Reject',
        privacy_policy_url: cfg.privacy_policy_url || '',
        layout: cfg.layout || 'bottom',
        retention_days: cfg.retention_days || 365,
        vendors: Array.isArray(cfg.vendors) ? cfg.vendors : [],
      });
      setExtraCountriesInput((cfg.extra_countries || []).join(', '));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Failed to load consent config'));
    }
  };

  const loadRecords = async () => {
    try {
      const r = await axios.get(`${API}/consent/records`, { params: { limit: 200 } });
      setRecords(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      // Records are best-effort — a fresh workspace has none, and a
      // 5xx here shouldn't block the editor.
      // eslint-disable-next-line no-console
      console.warn('consent records load failed', e);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadConfig(), loadRecords()]);
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const addVendor = () => {
    setForm((prev) => ({
      ...prev,
      vendors: [...prev.vendors, { name: '', purpose: '' }],
    }));
  };

  const removeVendor = (idx) => {
    setForm((prev) => ({
      ...prev,
      vendors: prev.vendors.filter((_, i) => i !== idx),
    }));
  };

  const updateVendor = (idx, key, value) => {
    setForm((prev) => ({
      ...prev,
      vendors: prev.vendors.map((v, i) => (i === idx ? { ...v, [key]: value } : v)),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const extras = extraCountriesInput
        .split(/[,\s]+/)
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);

      const payload = {
        enabled: form.enabled,
        region_preset: form.region_preset,
        extra_countries: extras,
        title: form.title || null,
        body: form.body || null,
        accept_btn_text: form.accept_btn_text || null,
        reject_btn_text: form.reject_btn_text || null,
        privacy_policy_url: form.privacy_policy_url || null,
        layout: form.layout,
        retention_days: Number(form.retention_days) || 365,
        vendors: form.vendors
          .filter((v) => v && v.name && v.name.trim())
          .map((v) => ({ name: v.name.trim(), purpose: (v.purpose || '').trim() })),
      };

      await axios.put(`${API}/consent/config`, payload);
      toast.success(t('Consent banner saved'));
      await loadConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-8 pt-2 pb-10 max-w-4xl">
        <p className="text-muted-foreground">{t('Loading…')}</p>
      </div>
    );
  }

  return (
    <div className="px-8 pt-2 pb-10 max-w-4xl space-y-6">
      <XpnsrPageHeader
        kicker={t('Compliance')}
        title={t('Consent banner')}
        subtitle={t(
          'Configure the GDPR / CCPA cookie banner that runs on your tracker landing pages. The banner is region-aware — visitors outside the configured regions never see it.',
        )}
      />

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="w-4 h-4" /> {t('Banner status')}
              </CardTitle>
              <CardDescription>
                {t(
                  'When disabled, the public widget responds with required:false so landing pages skip rendering entirely.',
                )}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="consent-enabled" className="text-sm">
                {form.enabled ? t('Enabled') : t('Disabled')}
              </Label>
              <Switch
                id="consent-enabled"
                checked={form.enabled}
                onCheckedChange={(v) => setField('enabled', !!v)}
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('Region scope')}</CardTitle>
          <CardDescription>
            {t(
              'Choose a preset and optionally extend it with extra ISO-3166 country codes (comma-separated, e.g. BR, MX, AR).',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>{t('Region preset')}</Label>
            <Select
              value={form.region_preset}
              onValueChange={(v) => setField('region_preset', v)}
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REGION_PRESETS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="extra-countries">{t('Extra countries (ISO-3166 alpha-2)')}</Label>
            <Input
              id="extra-countries"
              className="mt-2"
              placeholder="BR, MX, AR"
              value={extraCountriesInput}
              onChange={(e) => setExtraCountriesInput(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('Banner copy')}</CardTitle>
          <CardDescription>{t('Title, body, and button labels shown to the visitor.')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="consent-title">{t('Title')}</Label>
            <Input
              id="consent-title"
              className="mt-2"
              maxLength={200}
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="consent-body">{t('Body')}</Label>
            <Textarea
              id="consent-body"
              className="mt-2 min-h-[100px]"
              maxLength={2000}
              value={form.body}
              onChange={(e) => setField('body', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="consent-accept">{t('Accept button label')}</Label>
              <Input
                id="consent-accept"
                className="mt-2"
                maxLength={80}
                value={form.accept_btn_text}
                onChange={(e) => setField('accept_btn_text', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="consent-reject">{t('Reject button label')}</Label>
              <Input
                id="consent-reject"
                className="mt-2"
                maxLength={80}
                value={form.reject_btn_text}
                onChange={(e) => setField('reject_btn_text', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="consent-policy">{t('Privacy policy URL')}</Label>
            <Input
              id="consent-policy"
              className="mt-2"
              type="url"
              placeholder="https://example.com/privacy"
              value={form.privacy_policy_url}
              onChange={(e) => setField('privacy_policy_url', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('Layout & retention')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('Layout')}</Label>
              <Select value={form.layout} onValueChange={(v) => setField('layout', v)}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LAYOUTS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="consent-retention">{t('Record retention (days)')}</Label>
              <Input
                id="consent-retention"
                type="number"
                min={1}
                max={3650}
                className="mt-2"
                value={form.retention_days}
                onChange={(e) => setField('retention_days', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>{t('Vendor list')}</CardTitle>
              <CardDescription>
                {t('Third-party processors disclosed to the visitor (max 50).')}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addVendor}>
              <Plus className="w-4 h-4 mr-1" /> {t('Add vendor')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {form.vendors.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('No vendors disclosed.')}</p>
          ) : (
            form.vendors.map((vendor, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  placeholder={t('Vendor name (e.g. Google Analytics)')}
                  value={vendor.name}
                  onChange={(e) => updateVendor(idx, 'name', e.target.value)}
                  maxLength={80}
                />
                <Input
                  placeholder={t('Purpose (e.g. Analytics)')}
                  value={vendor.purpose}
                  onChange={(e) => updateVendor(idx, 'purpose', e.target.value)}
                  maxLength={200}
                />
                <Button variant="ghost" size="icon" aria-label={t('Remove vendor')} title={t('Remove vendor')} onClick={() => removeVendor(idx)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        <Button onClick={save} disabled={saving}>
          <Save className="w-4 h-4 mr-2" /> {saving ? t('Saving…') : t('Save')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> {t('Recent decisions')}
          </CardTitle>
          <CardDescription>
            {t('Last 200 consent records in this workspace. Used as the audit trail for DSAR / regulator inquiries.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-3">
            <Badge variant="default">{t('Accepted')}: {decisionCounts.accepted}</Badge>
            <Badge variant="secondary">{t('Rejected')}: {decisionCounts.rejected}</Badge>
            <Badge variant="outline">{t('Dismissed')}: {decisionCounts.dismissed}</Badge>
          </div>
          {records.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('No consent records yet.')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-2 pr-4">{t('Time')}</th>
                    <th className="py-2 pr-4">{t('Decision')}</th>
                    <th className="py-2 pr-4">{t('Country')}</th>
                    <th className="py-2 pr-4">{t('Visitor')}</th>
                    <th className="py-2 pr-4">{t('Tracking ID')}</th>
                  </tr>
                </thead>
                <tbody>
                  {records.slice(0, 50).map((r, idx) => (
                    <tr key={idx} className="border-t border-border">
                      <td className="py-2 pr-4 tabular-nums whitespace-nowrap">
                        {(r.timestamp || '').replace('T', ' ').slice(0, 19)}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant={r.decision === 'accepted' ? 'default' : 'secondary'}>
                          {r.decision}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">{r.country || '—'}</td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {r.visitor_id || r.click_id || '—'}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">{r.tracking_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ConsentEmbed />
    </div>
  );
}
